import React, { useMemo } from "react";
import { Card, Col, Placeholder, ProgressBar } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useFetchPaiementsQuery } from "features/paiements/paiementSlice";
import { useFetchEtudiantsQuery } from "features/etudiants/etudiantSlice";

const PaiementsWidget = () => {
  const { data: paiements = [], isLoading, isError } = useFetchPaiementsQuery();
  const { data: etudiants = [] } = useFetchEtudiantsQuery();

  const eleveMap = useMemo(() => {
    const map: Record<string, string> = {};
    etudiants.forEach((e: any) => {
      if (e._id) map[e._id] = `${e.prenom} ${e.nom}`;
    });
    return map;
  }, [etudiants]);

  const stats = useMemo(() => {
    const totalEleves = etudiants.length;
    const elevesAyantPaye = new Set(paiements.map((p) => p.eleve)).size;
    const totalEncaisse = paiements.reduce(
      (sum, p) => sum + (parseFloat(p.montant) || 0),
      0
    );
    const tauxRecouvrement =
      totalEleves > 0 ? Math.round((elevesAyantPaye / totalEleves) * 100) : 0;

    return { totalEleves, elevesAyantPaye, totalEncaisse, tauxRecouvrement };
  }, [paiements, etudiants]);

  const recentPaiements = useMemo(() => {
    return [...paiements]
      .sort((a, b) => {
        const da = new Date(a.date_paiement).getTime();
        const db = new Date(b.date_paiement).getTime();
        return db - da;
      })
      .slice(0, 5);
  }, [paiements]);

  const formatMontant = (m: string | number) => {
    const n = typeof m === "string" ? parseFloat(m) : m;
    return isNaN(n) ? "0" : n.toLocaleString("fr-TN") + " DT";
  };

  const formatDate = (d: string) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleDateString("fr-TN", {
        day: "2-digit",
        month: "short",
      });
    } catch {
      return d;
    }
  };

  return (
    <Col xxl={6} lg={12}>
      <Card className="card-height-100">
        <Card.Header className="d-flex align-items-center">
          <h5 className="card-title mb-0 flex-grow-1">
            <i className="ri-money-dollar-circle-line me-2 text-success" />
            Paiements
          </h5>
          <Link to="/paiement" className="btn btn-soft-success btn-sm">
            Voir tout <i className="ri-arrow-right-s-line" />
          </Link>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <Placeholder as="div" animation="glow">
              <Placeholder xs={12} className="mb-2" style={{ height: 40 }} />
              <Placeholder xs={8} className="mb-2" />
              <Placeholder xs={6} />
            </Placeholder>
          ) : isError ? (
            <div className="text-center text-danger py-3">
              <i className="ri-error-warning-line fs-2 d-block mb-2" />
              Impossible de charger les paiements
            </div>
          ) : (
            <>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted fs-13">Taux de recouvrement</span>
                  <span className="fw-semibold">{stats.tauxRecouvrement}%</span>
                </div>
                <ProgressBar
                  now={stats.tauxRecouvrement}
                  variant={
                    stats.tauxRecouvrement >= 75
                      ? "success"
                      : stats.tauxRecouvrement >= 50
                      ? "warning"
                      : "danger"
                  }
                  style={{ height: "8px" }}
                />
              </div>

              <div className="d-flex gap-3 mb-3">
                <div className="flex-fill text-center p-3 rounded bg-success-subtle">
                  <h5 className="text-success mb-1">
                    {formatMontant(stats.totalEncaisse)}
                  </h5>
                  <p className="text-muted mb-0 fs-13">Total encaissé</p>
                </div>
                <div className="flex-fill text-center p-3 rounded bg-primary-subtle">
                  <h5 className="text-primary mb-1">
                    {stats.elevesAyantPaye}/{stats.totalEleves}
                  </h5>
                  <p className="text-muted mb-0 fs-13">Élèves ayant payé</p>
                </div>
              </div>

              {recentPaiements.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm table-borderless align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="fs-13">Élève</th>
                        <th className="fs-13">Date</th>
                        <th className="fs-13 text-end">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentPaiements.map((p, i) => (
                        <tr key={i}>
                          <td className="text-truncate" style={{ maxWidth: 150 }}>
                            {eleveMap[p.eleve] || p.eleve}
                          </td>
                          <td className="text-muted fs-13">
                            {formatDate(p.date_paiement)}
                          </td>
                          <td className="text-end fw-medium">
                            {formatMontant(p.montant)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default PaiementsWidget;
